import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { emptyStateIconClass, viewAllLinkClass, widgetCardClass } from '../themeHelpers';

export interface GenericListWidgetProps<T = unknown> {
  title: string;
  viewAllPath: string;
  data: T[];
  isLoading: boolean;
  limit?: number;
  emptyMessage?: string;
  /** Render a single item. Receives item and optional index. */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Optional key extractor for list items (defaults to index). */
  getItemKey?: (item: T, index: number) => string | number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

function GenericListWidget<T>({
  title,
  viewAllPath,
  data,
  isLoading,
  limit = 5,
  emptyMessage = 'No items',
  renderItem,
  getItemKey,
  embedded,
}: GenericListWidgetProps<T>) {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const cardClass = widgetCardClass(colors, embedded);
  const listClass = embedded
    ? 'flex-1 min-h-0 overflow-y-auto space-y-1'
    : 'space-y-1 max-h-48 sm:max-h-64 overflow-y-auto';

  const items = Array.isArray(data) ? data.slice(0, limit) : [];

  if (isLoading) {
    return (
      <div className={cardClass}>
        <h2 className={`text-base font-semibold ${colors.text} mb-3 flex-shrink-0`}>{title}</h2>
        <div className={listClass}>
          {[1, 2, 3].map((item) => (
            <div
              key={item}
              className={`flex items-center space-x-3 pb-2 ${colors.border} border-b last:border-0`}
            >
              <div className={`h-8 w-8 rounded ${colors.surfaceHover} animate-pulse flex-shrink-0`} />
              <div className="flex-1 min-w-0">
                <div className={`h-3 ${colors.surfaceHover} animate-pulse rounded w-3/4 mb-1`} />
                <div className={`h-2.5 ${colors.surfaceHover} animate-pulse rounded w-1/2`} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={cardClass}>
      <div className="flex items-center justify-between mb-4 flex-shrink-0">
        <h2 className={`text-base font-semibold ${colors.text}`}>{title}</h2>
        <button
          type="button"
          onClick={() => navigate(viewAllPath)}
          className={viewAllLinkClass(colors)}
        >
          View All
        </button>
      </div>
      <div className={listClass}>
        {items.length > 0 ? (
          items.map((item, index) => (
            <React.Fragment key={getItemKey ? getItemKey(item, index) : index}>
              {renderItem(item, index)}
            </React.Fragment>
          ))
        ) : (
          <div className={`text-center py-8 ${colors.textMuted}`}>
            <svg
              className={emptyStateIconClass(colors)}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.5m-2.5 0h-5M4 13V6a2 2 0 012-2h2a2 2 0 012 2v7M4 13h2.5M4 13h5"
              />
            </svg>
            <p className="text-sm">{emptyMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default GenericListWidget;
